const PALETTE = ["#0F6CBD", "#7C3AED", "#059669", "#C2410C", "#BE185D", "#0E7490"];
export const authorColor = (a) => {
  let h = 0;
  for (const c of a || "") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

const textParts = (r) => {
  const base = r.commonAncestorContainer.nodeType === 3 ? r.commonAncestorContainer.parentNode : r.commonAncestorContainer;
  const out = [];
  const w = document.createTreeWalker(base, NodeFilter.SHOW_TEXT);
  let n;
  while ((n = w.nextNode())) {
    if (!r.intersectsNode(n) || n.parentElement.closest('[contenteditable="false"]')) continue;
    const s = n === r.startContainer ? r.startOffset : 0;
    const e = n === r.endContainer ? r.endOffset : n.data.length;
    if (s < e) out.push([n, s, e]);
  }
  return out;
};

const mark = (tag, author) => {
  const el = document.createElement(tag);
  el.className = tag === "ins" ? "tc-ins" : "tc-del";
  el.dataset.author = author;
  el.dataset.time = String(Date.now());
  el.style.setProperty("--tc", authorColor(author));
  return el;
};

const caret = (fn) => {
  const r = document.createRange();
  fn(r);
  r.collapse(true);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(r);
};

function markDeleted(root, r, author) {
  const anchors = [];
  textParts(r).forEach(([t, s, e]) => {
    if (t.parentElement.closest(".tc-del")) return anchors.push(t.parentElement.closest(".tc-del"));
    const x = s > 0 ? t.splitText(s) : t;
    if (e - s < x.data.length) x.splitText(e - s);
    if (x.parentElement.closest(".tc-ins")) {
      const m = document.createTextNode("");
      x.replaceWith(m);
      anchors.push(m);
    } else {
      const d = mark("del", author);
      x.replaceWith(d);
      d.appendChild(x);
      anchors.push(d);
    }
  });
  root.querySelectorAll(".tc-ins").forEach((i) => { if (!i.textContent) i.remove(); });
  return anchors.filter((a) => a.isConnected);
}

function insertTracked(text, author) {
  const sel = window.getSelection();
  let r = sel.getRangeAt(0);
  let n = r.startContainer;
  const del = (n.nodeType === 3 ? n.parentElement : n).closest(".tc-del");
  if (del) { r = document.createRange(); r.setStartAfter(del); r.collapse(true); n = r.startContainer; }
  const ins = (n.nodeType === 3 ? n.parentElement : n).closest(".tc-ins");
  if (ins && ins.dataset.author === author && n.nodeType === 3) {
    const off = r.startOffset;
    n.insertData(off, text);
    return caret((c) => c.setStart(n, off + text.length));
  }
  const el = mark("ins", author);
  el.textContent = text;
  r.insertNode(el);
  caret((c) => c.setStart(el.firstChild, text.length));
}

const caretPath = (root) => {
  const sel = window.getSelection();
  if (!sel.rangeCount || !root.contains(sel.anchorNode)) return null;
  const path = [];
  for (let n = sel.anchorNode; n && n !== root; n = n.parentNode) path.unshift([...n.parentNode.childNodes].indexOf(n));
  return { path, off: sel.anchorOffset };
};

export function createHistory(root) {
  const undo = [];
  const redo = [];
  const snap = () => ({ html: root.innerHTML, caret: caretPath(root) });
  const apply = (s) => {
    root.innerHTML = s.html;
    let n = root;
    for (const i of s.caret?.path || []) n = n?.childNodes[i];
    if (n) {
      try { caret((c) => c.setStart(n, Math.min(s.caret.off, n.nodeType === 3 ? n.data.length : n.childNodes.length))); } catch { /* caret best effort */ }
    }
    root.dispatchEvent(new Event("input", { bubbles: true }));
  };
  return {
    push() { undo.push(snap()); if (undo.length > 300) undo.shift(); redo.length = 0; },
    undo() { if (undo.length) { redo.push(snap()); apply(undo.pop()); } },
    redo() { if (redo.length) { undo.push(snap()); apply(redo.pop()); } },
  };
}

const flatten = (root) => {
  root.querySelectorAll(".tc-ins .tc-ins").forEach((i) => i.replaceWith(...i.childNodes));
  root.querySelectorAll(".tc-ins, .tc-del").forEach((i) => { if (!i.textContent && !i.querySelector("br,img")) i.remove(); });
};

export function trackHandler(root, isOn, getAuthor, history) {
  const fire = () => root.dispatchEvent(new Event("input", { bubbles: true }));
  return (e) => {
    if (!isOn()) return;
    const t = e.inputType;
    if (t === "historyUndo" || t === "historyRedo") { e.preventDefault(); return t === "historyUndo" ? history.undo() : history.redo(); }
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    history.push();
    const author = getAuthor();
    if (t === "insertParagraph" || t === "insertLineBreak") { setTimeout(() => flatten(root), 0); return; }
    if (["insertText", "insertReplacementText", "insertFromPaste", "insertFromDrop"].includes(t)) {
      const text = e.data ?? e.dataTransfer?.getData("text/plain") ?? "";
      if (!text) return;
      e.preventDefault();
      if (!sel.isCollapsed) {
        const a = markDeleted(root, sel.getRangeAt(0), author);
        if (a.length) caret((c) => c.setStartAfter(a[a.length - 1]));
      }
      insertTracked(text, author);
      return fire();
    }
    if (!t.startsWith("delete")) return;
    const back = t.includes("Backward");
    if (sel.isCollapsed) {
      const unit = t.includes("Word") ? "word" : t.includes("Line") ? "lineboundary" : "character";
      for (let i = 0; i < 300; i++) {
        const before = sel.getRangeAt(0).cloneRange();
        sel.modify("extend", back ? "backward" : "forward", unit);
        const r = sel.getRangeAt(0);
        if (r.collapsed) return e.preventDefault();
        const parts = textParts(r);
        if (!parts.length) { if (i === 0) { sel.removeAllRanges(); sel.addRange(before); return; } return e.preventDefault(); }
        if (parts.some(([n]) => !n.parentElement.closest(".tc-del"))) break;
        sel.collapse(back ? r.startContainer : r.endContainer, back ? r.startOffset : r.endOffset);
      }
    }
    e.preventDefault();
    const a = markDeleted(root, sel.getRangeAt(0), author);
    if (a.length) caret((c) => (back ? c.setStartBefore(a[0]) : c.setStartAfter(a[a.length - 1])));
    mergeDels(root);
    fire();
  };
}

function mergeDels(root) {
  root.querySelectorAll(".tc-del").forEach((d) => {
    const n = d.nextSibling;
    if (d.isConnected && n?.classList?.contains("tc-del") && n.dataset.author === d.dataset.author) { d.append(...n.childNodes); n.remove(); }
  });
}

export const changesIn = (root) => (root ? [...root.querySelectorAll(".tc-ins, .tc-del")] : []);

export function resolveChange(el, accept) {
  if (el.classList.contains("tc-ins") === accept) el.replaceWith(...el.childNodes);
  else el.remove();
}
